import React, { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { Send, X, Globe, Star, MessageSquare, Check, Phone } from "lucide-react";
import { saveKnowledgeBase, submitFeedback, generateIntroduction, scrapeWebsite } from "../apis/exploreChatbot";
import waVerifiedIcon from "../assets/waverified.webp";

const staticKnowledgeBase = `🏢 BluTec.AI — AI Business Automation

BluTec.AI is an AI-powered automation company helping businesses grow through sales, marketing, customer engagement, and communication.

🚀 Core Focus
Lead generation
Sales automation
WhatsApp & voice automation
Social media automation
Digital growth strategy

🌐 Website: www.blutec.ai

📦 Products
BluTec Scout – AI lead extractor (Google Maps & web)
BluTec Ping – WhatsApp automation & AI chatbots
BluTec Echo – AI voice calling agent
BluTec Orbit – AI social media content & scheduling

🛠 Services
Website & mobile app development
Digital marketing (SEO, Ads, Email)
CRM & sales automation
Cloud, APIs & custom software
AI & automation consulting

📞 Contact
📍 Ahmedabad, India
📧 tech@blutec.ai
📱 +91 99980 14013`;

const ExploreAIChatbot = ({ isOpen, onClose }) => {
  const [showSetup, setShowSetup] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [knowledgeBase, setKnowledgeBase] = useState(staticKnowledgeBase);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [isScraping, setIsScraping] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const MAX_MESSAGES = 5;

  // Reset state when component opens/closes
  useEffect(() => {
    if (isOpen) {
      setShowSetup(true);
      setPhoneNumber("");
      setMessages([]);
      setMessageCount(0);
      setShowRating(false);
      setRating(0);
      setFeedback("");
      setInputMessage("");
      setKnowledgeBase(staticKnowledgeBase);
    } else {
      // Disconnect socket when closed
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    // Initialize socket connection when component opens
    const SERVER_URL = process.env.REACT_APP_API_URL || "https://pingbk.blutec.ai";
    
    // Connect directly to the explore-chatbot namespace
    const exploreNamespace = io(`${SERVER_URL}/explore-chatbot`, {
      path: "/socket.io",
      transports: ["websocket", "polling"],
      reconnection: true,
    });

    // Store namespace reference
    socketRef.current = exploreNamespace;

    exploreNamespace.on("connect", () => {
      console.log("✅ Connected to explore chatbot socket");
    });

    exploreNamespace.on("ai_reply", (data) => {
      setIsLoading(false);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: data.message,
          sender: "ai",
          timestamp: new Date(),
        },
      ]);
      if (data.messageCount) {
        setMessageCount(data.messageCount);
      }
    });

    exploreNamespace.on("limit_reached", (data) => {
      setIsLoading(false);
      setShowRating(true);
      setMessageCount(data.messageCount || MAX_MESSAGES);
    });

    exploreNamespace.on("error", (error) => {
      setIsLoading(false);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: error.message || "Sorry, something went wrong. Please try again.",
          sender: "ai",
          timestamp: new Date(),
        },
      ]);
    });

    return () => {
      if (exploreNamespace) {
        exploreNamespace.disconnect();
      }
    };
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleScrapeWebsite = async () => {
    if (!websiteUrl.trim()) {
      alert("Please enter a valid URL");
      return;
    }

    setIsScraping(true);
    try {
      const response = await scrapeWebsite({ url: websiteUrl.trim() });
      if (response.data && response.data.success) {
        const newContent = response.data.content;
        // Truncate to 2000 characters if needed
        const MAX_LENGTH = 2000;
        const truncatedContent = newContent.length > MAX_LENGTH 
          ? newContent.substring(0, MAX_LENGTH) 
          : newContent;
        
        // Replace the entire knowledge base with scraped content
        setKnowledgeBase(truncatedContent);
        setWebsiteUrl("");
        
        // Show warning if content was truncated
        if (response.data.truncated || newContent.length > MAX_LENGTH) {
          alert(`Website content was truncated to ${MAX_LENGTH} characters. You can edit it below if needed.`);
        }
      } else {
        const errorMsg = response.data?.error || response.error || "Failed to scrape website";
        alert(errorMsg);
      }
    } catch (error) {
      console.error("Error scraping website:", error);
      const errorMessage = error.response?.data?.error || error.message || "Failed to fetch website content. Please check the URL and try again.";
      if (errorMessage.includes("limit") || errorMessage.includes("rate limit")) {
        alert(errorMessage);
      } else {
        alert("Failed to fetch website content. Please check the URL and try again.");
      }
    } finally {
      setIsScraping(false);
    }
  };

  const validatePhoneNumber = (phone) => {
    if (!phone || !phone.trim()) {
      return { valid: false, error: "Please enter your phone number" };
    }

    // Remove all spaces, dashes, and parentheses for validation
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, "");
    
    // More comprehensive phone number validation (7-15 digits)
    const phoneRegex = /^[+]?[0-9]{7,15}$/;
    
    if (!phoneRegex.test(cleanPhone)) {
      return { 
        valid: false, 
        error: "Please enter a valid phone number (7-15 digits, with optional country code like +91)" 
      };
    }

    return { valid: true, cleanPhone };
  };

  const staticTasks = `Rules and Guidelines:
1. Always be polite and professional
2. Provide accurate information based on knowledge base
3. If you don't know something, admit it politely
4. Keep responses concise and helpful
5. Use emojis appropriately to make conversations friendly`;

  const handleStartChatting = async () => {
    // Validate phone number
    const phoneValidation = validatePhoneNumber(phoneNumber);
    if (!phoneValidation.valid) {
      alert(phoneValidation.error);
      return;
    }

    const cleanPhone = phoneValidation.cleanPhone;
    const kbToUse = knowledgeBase.trim() || staticKnowledgeBase;
    
    if (!kbToUse) {
      alert("Please add a knowledge base first");
      return;
    }

    // Validate knowledge base length
    if (kbToUse.length > 2000) {
      alert("Knowledge base is too long. Maximum 2000 characters allowed. Please reduce the content.");
      return;
    }

    try {
      // Save knowledge base and phone number to server (creates lead)
      const response = await saveKnowledgeBase({ 
        phoneNumber: cleanPhone,
        knowledgeBase: kbToUse,
        tasksRules: staticTasks
      });

      // Check if feedback is required (different phone number or limit reached)
      if (response.data && response.data.requireFeedback) {
        // Close setup and show chat interface with feedback UI
        setShowSetup(false);
        setShowRating(true);
        setMessageCount(MAX_MESSAGES); // Set to max to show limit reached
        // Add a message explaining the situation
        setMessages([
          {
            id: Date.now(),
            text: response.data.error || "You have already used a different phone number or reached the limit. Please provide your feedback below.",
            sender: "ai",
            timestamp: new Date(),
          },
        ]);
        return;
      }

      setShowSetup(false);
      setIsLoading(true);

      // Generate AI-based introduction from knowledge base (REQUIRED - no fallback)
      try {
        const introResponse = await generateIntroduction(kbToUse);
        if (introResponse.data && introResponse.data.success && introResponse.data.message) {
          setMessages([
            {
              id: Date.now(),
              text: introResponse.data.message,
              sender: "ai",
              timestamp: new Date(),
            },
          ]);
          setIsLoading(false);
        } else {
          // If AI generation fails, show error and don't start chat
          setIsLoading(false);
          alert("Failed to generate AI introduction. Please try again.");
          setShowSetup(true);
        }
      } catch (introError) {
        console.error("Error generating introduction:", introError);
        // If AI generation fails, show error and don't start chat
        setIsLoading(false);
        alert("Failed to generate AI introduction. Please try again.");
        setShowSetup(true);
      }
    } catch (error) {
      console.error("Error saving knowledge base:", error);
      const errorMessage = error.response?.data?.error || error.message || error.error || "Failed to start chat. Please try again.";
      
      // Check if error indicates feedback is required
      if (error.response?.data?.requireFeedback || error.requireFeedback || errorMessage.includes("different phone number") || errorMessage.includes("limit") || errorMessage.includes("reached the limit")) {
        // Close setup and show chat interface with feedback UI
        setShowSetup(false);
        setShowRating(true);
        setMessageCount(MAX_MESSAGES); // Set to max to show limit reached
        setMessages([
          {
            id: Date.now(),
            text: errorMessage,
            sender: "ai",
            timestamp: new Date(),
          },
        ]);
      } else {
        alert(errorMessage);
        setIsLoading(false);
      }
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    if (messageCount >= MAX_MESSAGES) {
      if (!showRating) {
        setShowRating(true);
      }
      return;
    }

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage("");
    setIsLoading(true);
    setMessageCount((prev) => prev + 1);

    try {
      // Send message to server via socket
      const kbToUse = knowledgeBase.trim() || staticKnowledgeBase;
      const phoneValidation = validatePhoneNumber(phoneNumber);
      const cleanPhone = phoneValidation.valid ? phoneValidation.cleanPhone : "";
      
      if (socketRef.current && socketRef.current.connected) {
        socketRef.current.emit("chat_message", {
          message: messageToSend,
          knowledgeBase: kbToUse,
          phoneNumber: cleanPhone,
        });
      } else {
        throw new Error("Socket not connected");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setIsLoading(false);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: "Failed to send message. Please try again.",
          sender: "ai",
          timestamp: new Date(),
        },
      ]);
    }
  };

  const handleSubmitFeedback = async () => {
    if (rating === 0) {
      alert("Please select a rating");
      return;
    }

    setIsSubmittingFeedback(true);
    try {
      const phoneValidation = validatePhoneNumber(phoneNumber);
      const cleanPhone = phoneValidation.valid ? phoneValidation.cleanPhone : "";
      
      await submitFeedback({
        rating,
        feedback: feedback.trim() || "",
        phoneNumber: cleanPhone,
      });
      alert("Thank you for your feedback!");
      setShowRating(false);
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      alert("Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      {/* Close Button - Outside Phone Frame */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
        aria-label="Close"
      >
        <X size={24} className="text-gray-800" />
      </button>
      
      <div className="relative">
        {showSetup ? (
          <div className="w-[340px] bg-white rounded-[3rem] shadow-2xl overflow-hidden">
            {/* iPhone Frame for Setup */}
            <div className="bg-black rounded-[3rem] p-2">
              {/* iPhone Notch */}
              <div className="relative h-7 bg-black rounded-t-[2.5rem] flex items-center justify-center">
                {/* Notch */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-black rounded-b-3xl flex items-center justify-center">
                  {/* Speaker */}
                  <div className="w-16 h-1.5 bg-gray-800 rounded-full"></div>
                  {/* Camera */}
                  <div className="absolute right-4 w-2 h-2 bg-gray-800 rounded-full"></div>
                </div>
              </div>

              {/* Screen */}
              <div className="bg-white rounded-[2.5rem] overflow-hidden" style={{ height: "600px" }}>
                {/* Status Bar */}
                <div className="bg-[#075E54] text-white px-5 py-2 flex items-center justify-between text-[10px]">
                  <span>9:41</span>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-1.5 border border-white rounded-sm"></div>
                    <div className="w-0.5 h-0.5 bg-white rounded-full"></div>
                  </div>
                </div>

                {/* Setup Content */}
                <div className="px-5 py-5 h-[calc(600px-45px)] overflow-y-auto">
                  <div className="mb-5">
                    <h2 className="text-lg font-bold text-gray-900">
                      Setup Your AI Chatbot
                    </h2>
                  </div>

                  <div className="space-y-5">
                    {/* Phone Number Input */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="+91 99980 14013"
                        className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#01CC54] text-xs"
                      />
                      <p className="text-[10px] text-gray-500 mt-1">
                        Enter your phone number to start chatting
                      </p>
                    </div>

                    {/* Static Knowledge Base */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-2">
                        Knowledge Base
                      </label>
                      <textarea
                        value={knowledgeBase}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.length <= 2000) {
                            setKnowledgeBase(value);
                          }
                        }}
                        placeholder="Enter your knowledge base..."
                        maxLength={2000}
                        className={`w-full h-32 p-3.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#01CC54] resize-none text-xs ${
                          knowledgeBase.length > 2000 
                            ? "border-red-500" 
                            : knowledgeBase.length > 1800 
                            ? "border-yellow-500" 
                            : "border-gray-300"
                        }`}
                      />
                      <div className="flex justify-between items-center mt-1">
                        <p className="text-[10px] text-gray-500">
                          Maximum 2000 characters
                        </p>
                        <p className={`text-[10px] font-semibold ${
                          knowledgeBase.length > 2000 
                            ? "text-red-500" 
                            : knowledgeBase.length > 1800 
                            ? "text-yellow-600" 
                            : "text-gray-600"
                        }`}>
                          {knowledgeBase.length}/2000
                        </p>
                      </div>
                    </div>

                    {/* Static Tasks/Rules */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-2">
                        Tasks and Rules (Static - For Testing)
                      </label>
                      <textarea
                        value={staticTasks}
                        readOnly
                        className="w-full h-24 p-3.5 border border-gray-300 rounded-xl bg-gray-50 resize-none text-xs"
                      />
                    </div>

                    {/* Website Scraper */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-2">
                        Or Fetch Knowledge Base from Website
                      </label>
                      <div className="flex gap-2.5">
                        <input
                          type="url"
                          value={websiteUrl}
                          onChange={(e) => setWebsiteUrl(e.target.value)}
                          placeholder="https://example.com"
                          className="flex-1 px-3.5 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#01CC54] text-xs"
                        />
                        <button
                          onClick={handleScrapeWebsite}
                          disabled={isScraping || !websiteUrl.trim()}
                          className="px-4 py-2.5 bg-[#01CC54] text-white rounded-xl font-semibold hover:bg-[#01CC54]/90 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-1.5 text-xs"
                        >
                          <Globe size={14} />
                          {isScraping ? "..." : "Fetch"}
                        </button>
                      </div>
                    </div>

                    {/* Start Chatting Button */}
                    <button
                      onClick={handleStartChatting}
                      className="w-full py-3.5 bg-[#01CC54] text-white rounded-xl font-bold text-sm hover:bg-[#01CC54]/90 transition-all shadow-lg mt-5"
                    >
                      Start Chatting
                    </button>
                  </div>
                </div>
              </div>

              {/* Home Indicator */}
              <div className="h-1.5 bg-black rounded-full mx-auto mt-2 w-28"></div>
            </div>
          </div>
        ) : (
          <div className="w-[340px]">
            {/* iPhone Frame */}
            <div className="bg-black rounded-[3rem] p-2 shadow-2xl">
              {/* iPhone Notch */}
              <div className="relative h-7 bg-black rounded-t-[2.5rem] flex items-center justify-center">
                {/* Notch */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-black rounded-b-3xl flex items-center justify-center">
                  {/* Speaker */}
                  <div className="w-16 h-1.5 bg-gray-800 rounded-full"></div>
                  {/* Camera */}
                  <div className="absolute right-4 w-2 h-2 bg-gray-800 rounded-full"></div>
                </div>
              </div>

              {/* Screen */}
              <div className="bg-white rounded-[2.5rem] overflow-hidden" style={{ height: "600px" }}>

                {/* Chat Header - WhatsApp Style */}
                <div className="bg-[#075E54] text-white">
                  {/* Profile Section */}
                  <div className="px-4 pt-3 pb-3 flex items-center gap-3">
                    {/* Profile Picture */}
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <img
                        src="/i3.png"
                        alt="AI Logo"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    {/* Name and Phone */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <h3 className="font-semibold text-[13px] leading-tight">Blutec Ping</h3>
                        {/* WhatsApp Verified Badge */}
                        <img
                          src={waVerifiedIcon}
                          alt="WhatsApp Verified"
                          className="w-[16px] h-[16px] flex-shrink-0 transition-transform duration-300 hover:scale-110"
                          title="WhatsApp Verified"
                        />
                      </div>
                      <p className="text-[10px] text-white/90 font-normal">+91 99980 14013</p>
                    </div>

                    {/* Call Button */}
                    <a
                      href="tel:919998014013"
                      className="p-2 hover:bg-white/10 rounded-full transition-colors flex-shrink-0"
                      title="Call"
                    >
                      <Phone className="w-5 h-5 text-white" />
                    </a>
                  </div>
                </div>

                {/* Messages Container */}
                <div
                  ref={chatContainerRef}
                  className="bg-[#ECE5DD] flex-1 overflow-y-auto px-4 pt-4 pb-2"
                  style={{ height: "calc(600px - 140px)" }}
                >
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex mb-3.5 ${
                        msg.sender === "user" ? "justify-end" : "justify-start"
                      }`}
                    >
                      <div
                        className={`max-w-[75%] rounded-lg px-3 py-2 ${
                          msg.sender === "user"
                            ? "bg-[#DCF8C6] text-gray-900"
                            : "bg-white text-gray-900 shadow-sm"
                        }`}
                      >
                        <p className="text-xs whitespace-pre-wrap leading-relaxed">{msg.text}</p>
                        <p className="text-[10px] text-gray-500 mt-1">
                          {msg.timestamp.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                    </div>
                  ))}

                  {isLoading && (
                    <div className="flex justify-start mb-3.5">
                      <div className="bg-white rounded-lg px-3 py-2 shadow-sm">
                        <div className="flex gap-1">
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
                        </div>
                      </div>
                    </div>
                  )}

                  {messageCount >= MAX_MESSAGES && !showRating && (
                    <div className="flex justify-center mb-3.5">
                      <div className="bg-yellow-100 border border-yellow-300 rounded-lg px-3.5 py-2.5">
                        <p className="text-xs text-yellow-800">
                          You've reached the limit of {MAX_MESSAGES} messages. Please provide your feedback.
                        </p>
                      </div>
                    </div>
                  )}

                  {showRating && (
                    <div className="bg-white rounded-lg p-4 shadow-lg mb-3.5">
                      <h4 className="font-semibold mb-3 text-center text-xs">Rate Your Experience</h4>
                      <div className="flex justify-center gap-2 mb-3">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => setRating(star)}
                            className="p-0.5"
                          >
                            <Star
                              size={24}
                              className={star <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}
                            />
                          </button>
                        ))}
                      </div>
                      <textarea
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        placeholder="Share your feedback (optional)..."
                        className="w-full p-2.5 border border-gray-300 rounded-lg mb-3 resize-none text-xs"
                        rows="2"
                      />
                      <button
                        onClick={handleSubmitFeedback}
                        disabled={isSubmittingFeedback || rating === 0}
                        className="w-full py-2 bg-[#01CC54] text-white rounded-lg font-semibold hover:bg-[#01CC54]/90 disabled:bg-gray-300 disabled:cursor-not-allowed text-xs"
                      >
                        {isSubmittingFeedback ? "Submitting..." : "Submit Feedback"}
                      </button>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="bg-white border-t border-gray-200 px-4 py-3 pb-4">
                  {messageCount < MAX_MESSAGES ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                        placeholder="Type a message..."
                        className="flex-1 px-4 py-2.5 bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-[#01CC54] text-xs"
                        disabled={isLoading}
                      />
                      <button
                        onClick={handleSendMessage}
                        disabled={!inputMessage.trim() || isLoading}
                        className="p-2.5 bg-[#01CC54] text-white rounded-full hover:bg-[#01CC54]/90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      >
                        <Send size={18} />
                      </button>
                    </div>
                  ) : (
                    <div className="text-center text-xs text-gray-500 py-2">
                      <MessageSquare size={14} className="inline mr-1.5" />
                      Chat limit reached ({messageCount}/{MAX_MESSAGES})
                    </div>
                  )}
                </div>
              </div>

              {/* Home Indicator */}
              <div className="h-1.5 bg-black rounded-full mx-auto mt-2 w-28"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExploreAIChatbot;

